/**
 * O que a folha de aprovação de um pedido de compra escreve, antes de virar
 * papel.
 *
 * Papel: aqui ficam `PedidoNaFolha`, o pedido como a `FolhaDeAprovacao` o lê —
 * texto já resolvido, números já lidos e a foto de cada item já em mãos —, as
 * duas conversões que o produzem, a do pedido gravado (`pedidoGravadoParaFolha`)
 * e a do rascunho do formulário (`rascunhoParaFolha`), a busca das fotos
 * (`buscarFotosDaFolha`), as contas do bloco de ajustes e o nome de arquivo
 * sugerido na janela de impressão.
 *
 * Como usar: quem quer imprimir converte, busca as fotos e só então monta a
 * folha. A conversão deixa em `fotoGravada` o `id` do item cuja imagem está no
 * servidor, e a folha nunca a busca sozinha: montada antes de
 * `buscarFotosDaFolha` cumprir, ela abriria a impressão com a coluna da foto
 * vazia.
 *
 * Os valores da folha são os de `valorFinalDoItem`, `totalBrutoDoPedido` e
 * `totalDoPedido`, os mesmos do quadro e do formulário. A quantidade vazia ou
 * zero de um rascunho vale 1 na folha — na linha e no total —, para a linha
 * nunca sair com total zero enquanto a pessoa ainda digita.
 */
import {
  type ItemComValor,
  type PedidoCompra,
  dinheiroDoCampo,
  totalBrutoDoPedido,
  totalDoPedido,
} from '@concierge/shared';

import { formatarDinheiro, formatarInstante } from '@/shared/lib/formato';
import { fornecedorDoPedido } from './resumo';
import { ROTULO_SEM_RESIDENCIA, ROTULOS_CANAL } from './rotulos';
import { type PedidoNoFormulario } from './types';

/** Um item na folha, com a foto já em mãos. */
export interface ItemNaFolha extends ItemComValor {
  descricao: string;
  /** O fornecedor do item, escrito sob a descrição na folha detalhada. */
  fornecedor: string | null;
  /** A imagem em `data:`, ou `null` para a célula da foto sair vazia. */
  foto: string | null;
}

/** Um pedido na folha, com tudo o que ela escreve já resolvido. */
export interface PedidoNaFolha {
  numero: string;
  /** O nome da casa, `ROTULO_SEM_RESIDENCIA`, ou vazio no rascunho sem escolha. */
  casa: string;
  solicitante: string;
  /** A data da solicitação em ISO, ou vazio. */
  data: string;
  /** O canal de origem, já no rótulo da tela. */
  canal: string;
  /** A observação, ou `null` quando não há o que escrever. */
  observacao: string | null;
  itens: ItemNaFolha[];
}

/** Um item antes da busca das fotos. */
export interface ItemParaFolha extends ItemNaFolha {
  /** O `id` do item cuja imagem ainda está no servidor, ou `null`. */
  fotoGravada: string | null;
}

/** Um pedido antes da busca das fotos. */
export interface PedidoParaFolha extends Omit<PedidoNaFolha, 'itens'> {
  itens: ItemParaFolha[];
}

/** O que `rascunhoParaFolha` precisa saber além dos valores do formulário. */
export interface ContextoDoRascunho {
  /** As casas que o formulário oferece, para escrever o nome da escolhida. */
  casas: readonly { id: string; nome: string }[];
  /** O pedido gravado que o formulário edita, ou `null` num pedido novo. */
  gravado: PedidoCompra | null;
}

/** O minus da nota de desconto, o mesmo `−` (U+2212) da folha. */
const MENOS = '−';

/**
 * A quantidade como a folha a conta: zero, ou o que não se leu como número, vale 1.
 *
 * @param quantidade - A quantidade lida do item.
 * @returns A quantidade, ou 1.
 */
function quantidadeNaFolha(quantidade: number): number {
  return Number.isFinite(quantidade) && quantidade !== 0 ? quantidade : 1;
}

/**
 * Converte um pedido gravado para a folha.
 *
 * Quem chama é o quadro, com o pedido que a API acabou de confirmar. Os itens
 * com `temFoto` ficam com `fotoGravada` preenchido, à espera de
 * `buscarFotosDaFolha`.
 *
 * @param pedido - O pedido como a listagem o traz, com os itens aninhados.
 * @returns O pedido pronto para a busca das fotos.
 */
export function pedidoGravadoParaFolha(pedido: PedidoCompra): PedidoParaFolha {
  return {
    numero: pedido.numero,
    casa: pedido.residencia?.nome ?? ROTULO_SEM_RESIDENCIA,
    solicitante: pedido.solicitante,
    data: pedido.data,
    canal: ROTULOS_CANAL[pedido.canalOrigem],
    observacao:
      pedido.observacao !== null && pedido.observacao !== '' ? pedido.observacao : null,
    itens: pedido.itens.map((item) => ({
      descricao: item.descricao,
      quantidade: quantidadeNaFolha(item.quantidade),
      valorUnitario: item.valorUnitario,
      desconto: item.desconto,
      acrescimo: item.acrescimo,
      fornecedor: item.fornecedor,
      foto: null,
      fotoGravada: item.temFoto ? item.id : null,
    })),
  };
}

/**
 * Converte o que está no formulário para a folha, sem gravar nada.
 *
 * Quem chama é a tela do formulário, no "🖨 Imprimir / PDF" e no `Chip` que
 * tira o rascunho da cotação. A foto escolhida nesta sessão já está no campo e
 * vai direto; a do item gravado que ninguém trocou não está — o formulário não
 * a carrega ao abrir —, e fica em `fotoGravada` para a busca.
 *
 * @param valores - Os valores do formulário, antes do schema normalizar.
 * @param contexto - As casas oferecidas e o pedido gravado, quando há um.
 * @returns O rascunho pronto para a busca das fotos.
 */
export function rascunhoParaFolha(
  valores: PedidoNoFormulario,
  contexto: ContextoDoRascunho,
): PedidoParaFolha {
  const residenciaId = valores.residenciaId;
  const casa =
    residenciaId === null
      ? ROTULO_SEM_RESIDENCIA
      : (contexto.casas.find((uma) => uma.id === residenciaId)?.nome ?? '');
  const observacao = valores.observacao ?? '';

  return {
    numero: valores.numero ?? '',
    casa,
    solicitante: valores.solicitante ?? '',
    data: valores.data ?? '',
    canal: ROTULOS_CANAL[valores.canalOrigem],
    observacao: observacao !== '' ? observacao : null,
    itens: (valores.itens ?? []).map((item) => {
      const gravado =
        item.id === undefined
          ? undefined
          : contexto.gravado?.itens.find((original) => original.id === item.id);
      const fotoNoCampo = typeof item.foto === 'string' && item.foto !== '' ? item.foto : null;
      return {
        descricao: item.descricao ?? '',
        quantidade: quantidadeNaFolha(dinheiroDoCampo(item.quantidade)),
        valorUnitario: dinheiroDoCampo(item.valorUnitario),
        desconto: dinheiroDoCampo(item.desconto),
        acrescimo: dinheiroDoCampo(item.acrescimo),
        fornecedor: item.fornecedor ?? null,
        foto: fotoNoCampo,
        // Ausente é "não mexa": só aí a imagem gravada continua sendo a do item.
        fotoGravada: item.foto === undefined && gravado?.temFoto === true ? gravado.id : null,
      };
    }),
  };
}

/**
 * Busca as imagens que ainda estão no servidor e devolve os pedidos completos.
 *
 * Quem chama é `useFolhaDeAprovacao`, antes de montar a folha. As buscas correm
 * juntas, e a falha de qualquer uma rejeita a promessa inteira: a folha não
 * deverá sair com a foto de um item faltando sem que ninguém saiba.
 *
 * @param pedidos - Os pedidos convertidos, com `fotoGravada` onde há imagem.
 * @param buscarFoto - Lê a imagem de um item pelo `id`, como `buscarFotoDoItem`.
 * @returns Os pedidos com a foto de cada item no lugar.
 */
export async function buscarFotosDaFolha(
  pedidos: readonly PedidoParaFolha[],
  buscarFoto: (itemId: string) => Promise<{ foto: string | null }>,
): Promise<PedidoNaFolha[]> {
  return Promise.all(
    pedidos.map(async (pedido) => ({
      ...pedido,
      itens: await Promise.all(
        pedido.itens.map(async ({ fotoGravada, ...item }) => {
          if (fotoGravada === null) return item;
          const { foto } = await buscarFoto(fotoGravada);
          return { ...item, foto };
        }),
      ),
    })),
  );
}

/**
 * O fornecedor que representa o pedido na folha.
 *
 * @param pedido - O pedido da folha.
 * @returns O do primeiro item que tenha um, ou `null`.
 */
export function fornecedorDaFolha(pedido: Pick<PedidoNaFolha, 'itens'>): string | null {
  return fornecedorDoPedido(pedido);
}

/**
 * O total líquido do pedido, o do "TOTAL DO PEDIDO" e do cabeçalho de cada
 * bloco do lote.
 *
 * @param pedido - O pedido da folha.
 * @returns A soma de `valorFinalDoItem` dos itens.
 */
export function totalDaFolha(pedido: Pick<PedidoNaFolha, 'itens'>): number {
  return totalDoPedido(pedido.itens);
}

/** As três linhas do bloco de ajustes; o bloco só existe com uma das duas últimas. */
export interface AjustesDoPedido {
  /** "Subtotal dos itens": a soma de valor × quantidade. */
  subtotal: number;
  /** "Descontos": a soma dos descontos dos itens. */
  descontos: number;
  /** "Acréscimos (frete/taxas)": a soma dos acréscimos dos itens. */
  acrescimos: number;
}

/**
 * As contas do bloco de ajustes sob a tabela de itens.
 *
 * @param pedido - O pedido da folha.
 * @returns As três somas, ou `null` quando nenhum item tem desconto nem
 *   acréscimo — e aí o bloco não é desenhado.
 */
export function ajustesDoPedido(pedido: Pick<PedidoNaFolha, 'itens'>): AjustesDoPedido | null {
  const descontos = pedido.itens.reduce((soma, item) => soma + item.desconto, 0);
  const acrescimos = pedido.itens.reduce((soma, item) => soma + item.acrescimo, 0);
  if (descontos <= 0 && acrescimos <= 0) return null;
  return { subtotal: totalBrutoDoPedido(pedido.itens), descontos, acrescimos };
}

/**
 * A nota de desconto e acréscimo escrita sob a descrição de um item.
 *
 * @param item - O item da folha.
 * @returns `Desconto: − R$ x · Acréscimo: + R$ y`, só com as partes maiores
 *   que zero, ou `null` quando o item não tem nenhuma.
 */
export function notaDeAjusteDoItem(
  item: Pick<ItemNaFolha, 'desconto' | 'acrescimo'>,
): string | null {
  const partes: string[] = [];
  if (item.desconto > 0) partes.push(`Desconto: ${MENOS} ${formatarDinheiro(item.desconto)}`);
  if (item.acrescimo > 0) partes.push(`Acréscimo: + ${formatarDinheiro(item.acrescimo)}`);
  return partes.length > 0 ? partes.join(' · ') : null;
}

/**
 * Tira de um nome de arquivo os caracteres que o sistema de arquivos recusa.
 *
 * @param nome - O nome montado.
 * @returns O nome sem `\ / : * ? " < > |`, com os espaços colapsados.
 */
function nomeSeguro(nome: string): string {
  return nome
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * O nome que a janela de impressão sugere ao salvar o PDF.
 *
 * O navegador tira esse nome do `document.title`, e é para lá que a
 * `FolhaDeAprovacao` o leva enquanto está montada. Um pedido dá
 * `Pedido de Compra - número - fornecedor - AAAA-MM-DD`, sem as partes vazias;
 * dois ou mais dão `Pedidos de Compra - Lote N pedidos - AAAA-MM-DD`. A barra
 * de `PC-001/2026` sai do nome, porque nenhum sistema de arquivos a aceita.
 *
 * @param pedidos - Os pedidos da folha, um ou mais.
 * @param hoje - O dia da geração, em `AAAA-MM-DD`.
 * @returns O nome, sem extensão.
 */
export function nomeDoArquivoDaFolha(pedidos: readonly PedidoNaFolha[], hoje: string): string {
  const [unico] = pedidos;
  if (pedidos.length === 1 && unico !== undefined) {
    const partes = ['Pedido de Compra', unico.numero, fornecedorDaFolha(unico) ?? '', hoje];
    return nomeSeguro(partes.filter((parte) => parte !== '').join(' - '));
  }
  return nomeSeguro(`Pedidos de Compra - Lote ${String(pedidos.length)} pedidos - ${hoje}`);
}

/**
 * O dia e a hora da geração, no relógio do escritório.
 *
 * @param geradaEm - O instante do pedido de impressão, em ISO 8601 com fuso.
 * @returns O dia em `AAAA-MM-DD` e a hora em `HH:MM`, no horário de Brasília.
 */
export function diaEHoraDaGeracao(geradaEm: string): { dia: string; hora: string } {
  const [data = '', hora = ''] = formatarInstante(geradaEm).split(' ');
  const [dd = '', mm = '', aaaa = ''] = data.split('/');
  return { dia: `${aaaa}-${mm}-${dd}`, hora };
}
